<script setup>
import { replaceUnderscoreWithSpace } from './helper/filterHelper.js';

import Button from 'dashboard/components-next/button/Button.vue';
import { Badge } from 'next/polaris/ui/badge';

defineProps({
  appliedFilters: { type: Array, default: () => [] },
  maxVisibleFilters: { type: Number, default: 2 },
  clearButtonLabel: { type: String, default: '' },
  moreFiltersLabel: { type: String, default: '' },
  showClearButton: { type: Boolean, default: true },
});

const emit = defineEmits(['clearFilters', 'openFilter']);

const shouldCapitalizeFirstLetter = key => {
  const lowercaseKeys = ['email'];
  return !lowercaseKeys.includes(key);
};

const formatOperatorLabel = operator => {
  const operators = {
    equal_to: 'is',
    not_equal_to: 'is not',
    contains: 'contains',
    does_not_contain: 'does not contain',
    is_present: 'is present',
    is_not_present: 'is not present',
    is_greater_than: 'is greater than',
    is_less_than: 'is less than',
    days_before: 'days before',
  };
  return operators[operator] || replaceUnderscoreWithSpace(operator);
};

const formatFilterValue = value => {
  // Case 1: null, undefined, empty string
  if (!value) return '';

  // Case 2: array → map each item, use name if present, else the item itself
  if (Array.isArray(value)) {
    return value.map(item => item?.name ?? item).join(', ');
  }

  // Case 3: object with a "name" property → return name
  // Case 4: primitive (string, number, etc.) → return as is
  return value?.name ?? value;
};
</script>

<template>
  <div
    class="flex flex-wrap items-center w-full gap-2"
    role="group"
    data-active-filters
  >
    <template v-for="(filter, index) in appliedFilters" :key="index">
      <div
        v-if="index < maxVisibleFilters"
        class="inline-flex items-center gap-1.5"
      >
        <Badge
          variant="secondary"
          data-active-filter
          class="min-w-0 max-w-72 gap-1 py-1 pl-2 pr-2 font-normal normal-case cursor-pointer hover:bg-polaris-secondary/70"
          @click="emit('openFilter')"
        >
          <span
            class="truncate lowercase first-letter:capitalize text-polaris-secondary-foreground"
          >
            {{ replaceUnderscoreWithSpace(filter.attributeKey) }}
          </span>
          <span class="shrink-0 lowercase text-polaris-muted-foreground">
            {{ formatOperatorLabel(filter.filterOperator) }}
          </span>
          <span
            v-if="filter.values"
            :title="formatFilterValue(filter.values)"
            class="min-w-0 truncate font-medium lowercase text-polaris-secondary-foreground"
            :class="{
              'first-letter:capitalize': shouldCapitalizeFirstLetter(
                filter.attributeKey
              ),
            }"
          >
            {{ formatFilterValue(filter.values) }}
          </span>
        </Badge>
        <span
          v-if="
            index < maxVisibleFilters - 1 && index < appliedFilters.length - 1
          "
          class="shrink-0 type-polaris-small-label uppercase text-polaris-muted-foreground"
        >
          {{ filter.queryOperator }}
        </span>
      </div>
    </template>
    <Button
      v-if="appliedFilters.length > maxVisibleFilters"
      variant="outline"
      size="xs"
      :label="moreFiltersLabel"
      data-more-filters
      @click="emit('openFilter')"
    />
    <Button
      v-if="showClearButton"
      :label="clearButtonLabel"
      size="xs"
      class="h-auto min-h-6 p-0"
      variant="link"
      @click="emit('clearFilters')"
    />
  </div>
</template>
